import type { TicketDetail, UpdateTicket } from '@servicetim/shared';
import { useUpdateTicket } from '@servicetim/ui/api';
import { useConflictHandler } from '@servicetim/ui/components';
import { useCallback, useEffect, useRef } from 'react';

export type TicketPatch = (body: UpdateTicket) => Promise<TicketDetail | undefined>;

/**
 * One `PATCH /tickets/{id}` entry point for every edit site of the detail screen. `If-Match`
 * is the latest version the screen has seen; a 412/409 opens the conflict dialog whose
 * "reload and retry" refetches the ticket (updating the version) and re-runs the patch.
 * Other errors are re-thrown for the caller to show. Resolves `undefined` when a conflict was
 * captured.
 */
export function useTicketPatch(
  ticket: TicketDetail | undefined,
  refetch: () => Promise<{ data?: TicketDetail | undefined } | unknown>,
) {
  const update = useUpdateTicket();
  const versionRef = useRef(ticket?.version);
  useEffect(() => {
    versionRef.current = ticket?.version;
  }, [ticket?.version]);

  const reload = useCallback(async () => {
    const result = await refetch();
    const version = (result as { data?: TicketDetail | undefined } | undefined)?.data?.version;
    if (version !== undefined) versionRef.current = version;
  }, [refetch]);
  const conflict = useConflictHandler(reload);

  const ticketId = ticket?.id;
  const patch = useCallback<TicketPatch>(
    (body) =>
      conflict.guard(() => {
        if (ticketId === undefined || versionRef.current === undefined) {
          return Promise.reject(new Error('ticket not loaded'));
        }
        return update.mutateAsync({ id: ticketId, patch: body, ifMatch: versionRef.current });
      }),
    [conflict.guard, ticketId, update.mutateAsync],
  );

  return {
    patch,
    isPending: update.isPending || conflict.busy,
    dialogProps: conflict.dialogProps,
  };
}
